"use client";

import React, { useCallback, useEffect, useState } from "react";
import NextImage from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

import { LeftArrowIcon, RightArrowIcon } from "@/components/Icon";
import IconButton from "@/components/IconButton";
import styles from "../../styles.module.css";
import "../../styles.module.css";

const useCarouselButton = (emblaApi: EmblaCarouselType | undefined) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type Props = {
  images: Array<{
    src: string;
    alt?: string;
    placeholder?: "blur" | "empty";
  }>;
};
export default function ImagesCarousel({ images }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: false,
    active: true,
    loop: images.length > 2,
  });
  const [currentSlide, setCurrentSlide] = useState(1);
  const {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
    onNextButtonClick,
    onPrevButtonClick,
  } = useCarouselButton(emblaApi);

  return (
    <PhotoProvider loop speed={(type) => 100} photoClosable={true}>
      <figure className={`${styles.article__image__container}`}>
        <div className={`${styles.article__image__carousel} `} ref={emblaRef}>
          <div
            className={`${styles.article__image__container__carousel} ${
              images.length <= 2
                ? styles.article__image__container__carousel__two__image
                : ""
            }`}
          >
            {images.map(({ src, alt, placeholder }) => (
              <div
                className={styles.article__image__wrapper}
                // @ts-ignore
                key={src?.src || src}
              >
                {/* @ts-ignore */}
                <PhotoView src={src?.src || src}>
                  <NextImage
                    className={styles.article__image}
                    src={src}
                    alt={alt || ""}
                    fill
                    sizes={"100vw"}
                    // sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    placeholder={placeholder}
                  />
                </PhotoView>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.article__image__container__carousel__counter}>
          <IconButton
            Icon={LeftArrowIcon}
            className={
              styles.article__image__container__carousel__counter__button
            }
            onClick={onPrevButtonClick}
          />
          <span
            className={
              styles.article__image__container__carousel__counter__text
            }
          >
            {selectedIndex + 1} / {scrollSnaps.length}
          </span>
          <IconButton
            Icon={RightArrowIcon}
            className={
              styles.article__image__container__carousel__counter__button
            }
            onClick={onNextButtonClick}
          />
        </div>
      </figure>
    </PhotoProvider>
  );
}
